import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { checkAppHealth } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { discoverKvUri } from "../../lib/brain-paths.js";

export class BrainCheckAppCommand extends M8tCommand {
  static paths = [["brain", "check-app"]];
  static usage = Command.Usage({
    description: "Sanity-check the platform-level GitHub App configuration.",
    details:
      "5 checks: KV secrets readable, App ID numeric, private key valid, App JWT accepted by GitHub (slug matches KV), installations listable. No side effects. Run after setting up the App + KV secrets per deploy/github-app-registration-setup.md.",
    examples: [
      ["Default (uses AZURE_KEYVAULT_URI env)", "$0 brain check-app"],
      ["Explicit KV URI", "$0 brain check-app --kv-uri https://m8t-kv.vault.azure.net"],
    ],
  });

  kvUri = Option.String("--kv-uri");
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    // Normalize output: clipanion leaves a descriptor object on the property
    // until argv parsing; when set directly in tests (or not supplied), treat
    // anything that is not a plain string as "pretty" (our diagnostic default).
    const outputFlag =
      this.output === "json" || this.output === "auto" || this.output === "pretty"
        ? (this.output)
        : "pretty";
    const mode = resolveOutputMode(
      outputFlag,
      this.context.stdout as NodeJS.WriteStream,
    );
    const kvUri = discoverKvUri(process.env, typeof this.kvUri === "string" ? this.kvUri : undefined);
    const credential = new DefaultAzureCredential();
    const result = await checkAppHealth({ credential, kvUri });

    if (mode === "json") {
      this.context.stdout.write(renderJson(result) + "\n");
      return result.ok ? 0 : 1;
    }

    this.context.stdout.write(`${colors.field("GitHub App health check")} (${kvUri})\n\n`);
    const row = (label: string, c: { ok: boolean; error?: string; value?: string; slug?: string; installationsCount?: number; count?: number }) => {
      const status = c.ok ? colors.success("✓") : colors.error("✗");
      const detail = c.ok
        ? (c.value ?? c.slug ?? (c.count !== undefined ? `(${c.count.toString()} installations)` : c.installationsCount !== undefined ? `(${c.installationsCount.toString()} installations)` : ""))
        : (c.error ?? "(failed)");
      this.context.stdout.write(`  ${status} ${label.padEnd(28)} ${colors.dim(detail)}\n`);
    };
    row("KV secrets readable", result.checks.kvSecretsReadable);
    row("App ID numeric", result.checks.appIdNumeric);
    row("Private key valid", result.checks.privateKeyValid);
    row("App JWT accepted", result.checks.appJwtAccepted);
    row("Installations listable", result.checks.installationsListable);
    this.context.stdout.write("\n");
    this.context.stdout.write(
      result.ok ? `${colors.success("✓")} all checks passed.\n`
                : `${colors.error("✗")} one or more checks failed — see above.\n`,
    );
    return result.ok ? 0 : 1;
  }
}
